import React from 'react';
import { Box, Modal, Grid, Zoom, TableContainer, Table, TableHead, TableRow, TableCell, Paper, TableBody } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SwalUtil from '../../shared/util/sweetalert/sweetalertUtil';
import { BackdropLoading } from '../../shared/spinner/backdropLoading';
import { CancelButton, ConditionNameText, DeleteButton, ModalLabel, modalStyle, LoadButton } from './loadCondition.styles';
import { fetchAuthSession } from 'aws-amplify/auth';
import { SearchCrudApiRequest } from '../../shared/model/apiRequestModel';
import crud from '../../shared/api/crud';
import { SystemErrorMsg, SystemErrorTitle } from '../../constants';
import { sessionDisconnectedCheck } from '../../shared/api/sessionDisconnectedCheck';

interface OwnProps {
    isOpen: boolean,
    handleClose: (isOpen: boolean) => void
    handleLoad: (tableValue: LoadSearchCondition[]) => void,
}

interface LoadSearchCondition {
    operator: string
    itemCode: string
    itemName: string
    inputValue: string
    inputLabel?: string
    matchFlag?: boolean
}

interface ConditionsData {
    recordId: string,
    registDateTime: string,
    saveName: string,
    searchConditions: LoadSearchCondition[],
}

const screenId = "U042"

const LoadCondition = (props: OwnProps) => {
    const navigate = useNavigate();
    const { isOpen, handleClose } = props
    const [open, setOpen] = React.useState(isOpen);
    const [loadDataList, setLoadDataList] = React.useState<ConditionsData[]>([]);
    const [loading, setLoading] = React.useState<boolean>(false);

    const getDataList = async () => {
        // セッションの有効性確認
        if (await sessionDisconnectedCheck(screenId, "getDataList", navigate)) {
            return
        }
        setLoading(true)
        const session = await fetchAuthSession();
        const email = session.tokens?.idToken?.payload?.email?.toString() || '';
        const sessionId = session.tokens?.idToken?.payload?.sessionId?.toString() || '';
        const accountInfoData = JSON.parse(session.tokens?.idToken?.payload?.accountInfoData?.toString() || "[]");
        const contract = JSON.parse(session?.tokens?.idToken?.payload.contractInfoData?.toString() || "[]");
        const contractForm = contract.length !== 0 && contract[0].contractForm;
        const request: SearchCrudApiRequest = {
            sessionId: sessionId,
            userMailAddress: email,
            processFlag: "0",
            countFlag: "0",
            contractForm: contractForm,
            targetTableName: "T_SAVE_INPUT_DATA",
            selectItems: ['RECORD_ID', 'SAVE_NAME', 'KOSHIN_DATE', 'SEARCH_CONDITIONS'],
            conditions: [
                {
                    targetColumn: "MAIL_ADDRESS",
                    targetValue: [email]
                }
            ],
            conditionsOperator: 'and',
            orderBy: [
                {
                    sortColumn: 'TOROKU_DATE',
                    sortRule: 'ASC',
                }
            ],
            usingStartDate: contract.length !== 0 && accountInfoData[0].usingStartDate
        }
        const response = crud(request);
        const resCd = (await response).resCd;
        if (resCd === 'I0000') {
            const loadDataList: ConditionsData[] = [];
            const resDataList = (await response).data?.resultData;
            resDataList.forEach((v: { [key: string]: any }) => {
                loadDataList.push({
                    recordId: v.RECORD_ID.toString(),
                    registDateTime: v.KOSHIN_DATE,
                    saveName: v.SAVE_NAME || '',
                    searchConditions: JSON.parse(v.SEARCH_CONDITIONS),
                })
            })
            setLoadDataList(loadDataList)
            setLoading(false);
        } else {
            navigate("/Error", { state: { title: SystemErrorTitle, message: SystemErrorMsg } });
        }
    };

    React.useEffect(() => {
        if (isOpen) {
            getDataList()
        }
        setOpen(isOpen)
    }, [isOpen])

    const handleLoad = async (v: LoadSearchCondition[]) => {
        // セッションの有効性確認
        if (await sessionDisconnectedCheck(screenId, "handleLoad", navigate)) {
            return
        }
        SwalUtil.labelInfo({
            title: "読込完了",
            message: "保存データの読込が完了しました",
            icon: "success",
            confirmButtonText: "OK",
            buttonAlign: "center",
        }).then((result) => {
            if (result.isConfirmed) {
                props.handleLoad(v)
            }
        });
    }

    const handleDelete = async (recordId: string) => {
        // セッションの有効性確認
        if (await sessionDisconnectedCheck(screenId, "handleDelete", navigate)) {
            return
        }
        setLoading(true)
        const deleteConditions = async () => {
            const session = await fetchAuthSession();
            const email = session.tokens?.idToken?.payload?.email?.toString() || '';
            const sessionId = session.tokens?.idToken?.payload?.sessionId?.toString() || '';
            const accountInfoData = JSON.parse(session.tokens?.idToken?.payload?.accountInfoData?.toString() || "[]");
            const contract = JSON.parse(session?.tokens?.idToken?.payload.contractInfoData?.toString() || "[]");
            const contractForm = contract.length !== 0 && contract[0].contractForm;
            const request: SearchCrudApiRequest = {
                sessionId: sessionId,
                userMailAddress: email,
                processFlag: "9",
                countFlag: "0",
                contractForm: contractForm,
                targetTableName: "T_SAVE_INPUT_DATA",
                selectItems: [],
                conditions: [
                    {
                        targetColumn: "MAIL_ADDRESS",
                        targetValue: [email]
                    },
                    {
                        targetColumn: "RECORD_ID",
                        targetValue: [recordId]
                    }
                ],
                conditionsOperator: 'and',
                orderBy: [],
                usingStartDate: contract.length !== 0 && accountInfoData[0].usingStartDate
            }
            const response = crud(request);
            const resCd = (await response).resCd;
            if (resCd === 'I0000') {
                setLoading(false)
                SwalUtil.labelInfo({
                    title: "削除完了",
                    message: '保存データの削除が完了しました',
                    confirmButtonText: "OK",
                    buttonAlign: "right",
                    buttonOrder: { confirm: 2, deny: 1 }
                }).then(() => {
                    getDataList()
                });
            } else {
                navigate("/Error", { state: { title: SystemErrorTitle, message: SystemErrorMsg } });
            }
        };
        deleteConditions()
    }

    const formatDate = (strDate: string) => {
        const date = new Date(strDate)
        return date.toLocaleDateString('ja-JP', {
            timeZone: "UTC",
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    }

    return (
        <>
            <BackdropLoading open={loading} />
            <Modal
                open={open}
                onClose={handleClose}
                closeAfterTransition
                sx={{
                    zIndex: 900
                }}
                slotProps={{
                    backdrop: {
                        sx: {
                            zIndex: 900
                        }
                    }
                }}
            >
                <Zoom
                    in={open}
                    style={{ zIndex: 1000, transformOrigin: '-50% -50% -50%' }}
                    easing={{
                        enter: "cubic-bezier(0, 1.45, 0.62, 1)",
                        exit: "linear"
                    }}
                >
                    <Box sx={modalStyle} tabIndex={-1}>
                        <ModalLabel>
                            検索条件の読込・削除
                        </ModalLabel>
                        <Box marginTop={5} sx={{ color: '#808080', fontSize: '1.1rem' }}>
                            読み込む保存データを選択してください
                        </Box>
                        <Box p={5}>
                            <TableContainer component={Paper} sx={{ maxHeight: '17.2rem' }}>
                                <Table stickyHeader sx={{ borderCollapse: 'separate', "tr:nth-child(even)": { backgroundColor: "#F2F2F2" } }}>
                                    <TableHead>
                                        <TableRow sx={{ '& .MuiTableCell-root': { border: '1px solid gray', height: '2rem', fontSize: '1rem', paddingLeft: '0', paddingRight: '0', paddingTop: '0.2rem', paddingBottom: '0.2rem', textAlign: 'center' } }}>
                                            <TableCell style={{ width: '20%', backgroundColor: '#00A99C', color: '#FFFFFF' }}>保存日時</TableCell>
                                            <TableCell style={{ width: '80%', backgroundColor: '#00A99C', color: '#FFFFFF' }}>保存名</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {loadDataList.map((row) => (
                                            <TableRow sx={{ '& .MuiTableCell-root': { border: '1px solid gray', height: '0.3rem', fontSize: '1rem', paddingLeft: '0.5rem', paddingRight: '0.1rem', paddingTop: '0', paddingBottom: '0', } }}>
                                                <TableCell sx={{ textAlign: 'left' }}>{formatDate(row.registDateTime)}</TableCell>
                                                <TableCell sx={{ textAlign: 'left' }}>
                                                    <Grid container>
                                                        <ConditionNameText size={9} sx={{ paddingRight: '0.2rem' }}>
                                                            {row.saveName}
                                                        </ConditionNameText>
                                                        <Grid size={3}>
                                                            <LoadButton variant="contained" color="secondary" onClick={() => handleLoad(row.searchConditions)}>読込</LoadButton>
                                                            <DeleteButton variant="contained" color="secondary" onClick={() => handleDelete(row.recordId)}>削除</DeleteButton>
                                                        </Grid>
                                                    </Grid>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                        <Box p={2} sx={{ marginTop: '-3%', marginBottom: '1%' }}>
                            <Grid container sx={{ width: '98%' }}>
                                <Grid size={9} />
                                <Grid size={3}>
                                    <CancelButton variant="contained" onClick={() => handleClose(false)}>閉じる</CancelButton>
                                </Grid>
                            </Grid>
                        </Box>
                    </Box>
                </Zoom>
            </Modal >
        </>
    );
}

export default LoadCondition;
